import React, { useState } from "react";
import axios from "axios";
import {
  Search, User, Mail, Phone, Linkedin, MapPin, Building2, CheckCircle2,
  Plus, Loader2, X, AlertTriangle, UserCheck
} from "lucide-react";
import ApolloStyleFilters from "../../components/ApolloStyleFilters";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const AVATAR_PALETTE = ["#0891B2", "#7C3AED", "#DB2777", "#059669", "#B45309", "#0369A1"];
function avatarFor(name = "") {
  const initials = name.trim().split(/\s+/).slice(0, 2).map(s => s[0] || "").join("").toUpperCase() || "?";
  let h = 0; for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return { initials, color: AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length] };
}

function ResultCard({ p, onSave, savingKey, savedKeys }) {
  const key = p.email || p.name;
  const isSaved = savedKeys.has(key);
  const isSaving = savingKey === key;
  const { initials, color } = avatarFor(p.name);

  return (
    <div data-testid="people-result-card" className="group border border-white/[0.04] bg-[#0D0F12] hover:border-[#00E5FF]/40 transition-all duration-300 overflow-hidden">
      <div className="h-0.5 bg-gradient-to-r from-[#00E5FF] to-transparent" />
      <div className="p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center font-bold text-sm border" style={{ background: `${color}18`, borderColor: `${color}55`, color, fontFamily: "Chivo, sans-serif" }}>
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-white leading-tight truncate" style={{ fontFamily: "Chivo, sans-serif" }} title={p.name}>{p.name}</h3>
            {p.title && <p className="text-xs text-[#00E5FF] truncate mt-0.5">{p.title}</p>}
            {p.company && (
              <div className="mt-1 inline-flex items-center gap-1 border border-white/[0.04] bg-[#050505] px-1.5 py-0.5 max-w-full">
                <Building2 className="w-2.5 h-2.5 text-[#A0AEC0] flex-shrink-0" />
                <span className="text-[10px] text-[#CBD5E1] truncate">{p.company}</span>
              </div>
            )}
          </div>
        </div>

        {/* Contact details */}
        <div className="space-y-1 mb-4">
          {p.email && (
            <div className="flex items-center gap-1.5 text-xs text-[#CBD5E1]">
              <Mail className="w-3 h-3 text-[#00E5FF]" />
              <span className="truncate">{p.email}</span>
            </div>
          )}
          {p.phone && (
            <div className="flex items-center gap-1.5 text-xs text-[#CBD5E1]">
              <Phone className="w-3 h-3 text-[#16C784]" />
              <span>{p.phone}</span>
            </div>
          )}
          {p.location && (
            <div className="flex items-center gap-1.5 text-xs text-[#CBD5E1]">
              <MapPin className="w-3 h-3 text-[#F5B82E]" />
              <span className="truncate">{p.location}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 pt-3 border-t border-white/[0.04]">
          {p.linkedin && (
            <a href={p.linkedin} target="_blank" rel="noopener noreferrer"
               className="flex-shrink-0 flex items-center gap-1 px-2 py-2 border border-white/[0.04] bg-[#050505] hover:border-[#A78BFA]/40 hover:text-[#A78BFA] text-[#CBD5E1] transition-colors">
              <Linkedin className="w-3.5 h-3.5" />
            </a>
          )}
          <button
            data-testid={`people-save-${key}`}
            onClick={() => onSave(p)}
            disabled={isSaved || isSaving}
            className={`flex-1 flex items-center justify-center gap-2 py-2 text-[11px] font-bold tracking-widest uppercase transition-all ${
              isSaved
                ? "bg-[#16C784]/15 border border-[#16C784]/50 text-[#16C784]"
                : "bg-[#00E5FF] text-black hover:bg-white disabled:opacity-60"
            }`}
          >
            {isSaving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Aggiungo…</>
             : isSaved ? <><CheckCircle2 className="w-3.5 h-3.5" /> Aggiunto</>
             : <><Plus className="w-3.5 h-3.5" /> Aggiungi al database</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
export default function ProfileIDCard() {
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState({});
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [savingKey, setSavingKey] = useState(null);
  const [savedKeys, setSavedKeys] = useState(new Set());
  const [toast, setToast] = useState(null);

  const hasActiveFilters = Object.values(filters).some(v => v !== "" && v !== null && v !== undefined && v !== false);

  const doSearch = async (e) => {
    e?.preventDefault?.();
    const q = query.trim();
    // Allow searching with filters only (no query required)
    if (q.length < 2 && !hasActiveFilters) { setError("Inserisci almeno 2 caratteri o applica un filtro"); return; }
    setLoading(true); setError(""); setResults([]);
    try {
      const { data } = await axios.post(`${API}/people/search`, { query: q, filters }, { withCredentials: true });
      setResults(data.results || []);
      if ((data.results || []).length === 0) setError("Nessuna persona trovata. Prova con altri filtri o una query più ampia.");
    } catch (err) {
      setError(err.response?.data?.detail || "Errore durante la ricerca");
    } finally { setLoading(false); }
  };

  const handleSave = async (p) => {
    const key = p.email || p.name;
    setSavingKey(key);
    try {
      const { data } = await axios.post(`${API}/people/save`, { data: p }, { withCredentials: true });
      setSavedKeys(prev => new Set([...prev, key]));
      setToast({
        type: "success",
        title: data.already_existed ? "Profilo aggiornato" : "Aggiunto al database",
        msg: `${p.name} è ora disponibile in People List. Aprilo per consultare la scheda completa con OSINT · SOCMINT · HUMINT.`,
      });
      setTimeout(() => setToast(null), 6000);
    } catch (err) {
      setToast({ type: "error", title: "Errore", msg: err.response?.data?.detail || "Impossibile salvare il profilo" });
      setTimeout(() => setToast(null), 5000);
    } finally { setSavingKey(null); }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6">
      {/* Hero header */}
      <div>
        <div className="inline-flex items-center gap-2 mb-2 text-[10px] tracking-[0.25em] uppercase text-[#00E5FF] font-bold">
          <UserCheck className="w-3 h-3" /> Intelligence · People Check
        </div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight" style={{ fontFamily: "Chivo, sans-serif" }}>People Check</h1>
        <p className="text-sm mt-1 max-w-2xl" style={{ color: "#475569" }}>
          Cerca una persona per nome, ruolo, azienda o email. Con un click aggiungila alla <span className="text-[#00E5FF] font-semibold">People List</span> per abilitarne il monitoring OSINT · SOCMINT · HUMINT.
        </p>
      </div>

      {/* Search bar */}
      <form onSubmit={doSearch} className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
          <input
            data-testid="people-search-input"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Cerca una persona per nome, ruolo, azienda o email..."
            className="w-full bg-[#050505] border border-white/[0.04] text-white pl-10 pr-10 py-3 text-sm focus:outline-none focus:border-[#00E5FF] transition-colors placeholder:text-[#6B7280]"
            autoFocus
          />
          {query && (
            <button type="button" onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#6B7280] hover:text-white transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          type="submit"
          data-testid="people-search-button"
          disabled={loading}
          className="bg-[#00E5FF] text-black px-6 py-3 text-xs font-bold uppercase tracking-widest hover:bg-white transition-colors flex items-center gap-2 disabled:opacity-60"
        >
          {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Cerco…</> : <><Search className="w-3.5 h-3.5" /> Cerca persona</>}
        </button>
      </form>

      {/* Apollo-style header filters (collapsible) */}
      <ApolloStyleFilters
        entity="person"
        filters={filters}
        setFilters={setFilters}
        onApply={() => doSearch()}
      />

      {/* Error */}
      {error && (
        <div data-testid="people-error" className="border border-[#F5B82E]/40 bg-[#F5B82E]/10 px-4 py-3 flex items-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4 text-[#F5B82E] flex-shrink-0" />
          <span className="text-[#F5B82E]">{error}</span>
        </div>
      )}

      {/* Results header */}
      {results.length > 0 && (
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest pt-2" style={{ color: "#64748B" }}>
          <span><span className="font-bold tabular-nums" style={{ color: "#1E293B" }}>{results.length}</span> corrispondenze</span>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && results.length === 0 && (
        <div className="border border-dashed border-white/[0.04] bg-[#0D0F12] py-16 flex flex-col items-center justify-center text-center">
          <div className="w-14 h-14 border border-[#00E5FF]/40 bg-[#00E5FF]/10 flex items-center justify-center mb-4">
            <User className="w-7 h-7 text-[#00E5FF]" />
          </div>
          <p className="text-white font-semibold mb-1">Cerca una persona per iniziare</p>
          <p className="text-xs text-[#A0AEC0] max-w-md">
            Digita nome, ruolo (es. &quot;CFO&quot;), azienda o email. I risultati vengono dal Business Intelligence Network di MOONSTONE.
          </p>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {["a","b","c","d","e","f"].map((k) => (
            <div key={`skeleton-${k}`} className="border border-white/[0.04] bg-[#0D0F12] p-5">
              <div className="flex gap-3 mb-4">
                <div className="w-12 h-12 bg-[#1F242D] animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-[#1F242D] animate-pulse w-3/4" />
                  <div className="h-2 bg-[#1F242D] animate-pulse w-1/2" />
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="h-3 bg-[#1F242D] animate-pulse" />
                <div className="h-3 bg-[#1F242D] animate-pulse w-2/3" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Results grid */}
      {!loading && results.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {results.map((p, i) => (
            <ResultCard key={p.candidate_id || p.email || i} p={p} onSave={handleSave} savingKey={savingKey} savedKeys={savedKeys} />
          ))}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div data-testid="people-toast"
             className="fixed bottom-6 right-6 max-w-sm border p-4 shadow-2xl z-50"
             style={{
               borderColor: toast.type === "success" ? "#059669" : "#DC2626",
               background: toast.type === "success" ? "#0D0F12" : "#1A0B0B",
               boxShadow: `0 20px 60px -20px ${toast.type === "success" ? "#16C78455" : "#FF3B3055"}`,
             }}>
          <div className="flex items-start gap-3">
            {toast.type === "success" ? <CheckCircle2 className="w-5 h-5 text-[#16C784] flex-shrink-0" /> : <AlertTriangle className="w-5 h-5 text-[#FF3B30] flex-shrink-0" />}
            <div className="flex-1">
              <p className="text-sm font-bold text-white mb-0.5">{toast.title}</p>
              <p className="text-xs text-[#CBD5E1] leading-relaxed">{toast.msg}</p>
            </div>
            <button onClick={() => setToast(null)} className="text-[#A0AEC0] hover:text-white flex-shrink-0">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
